<template>
  <div class="archived-invoice__upload">
    <template v-if="disableCreateDispute">
      <Notification
        title="Dispute creation is in progress."
        icon="info-circle"
        type="info">
      </Notification>
    </template>

    <upload-attachment
      title="Upload Excel"
      :multiple="false"
      :maximum="1"
      :supported-file-types="[
        'application/vnd.ms-excel',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'message/rfc822',
      ]"
      supported-file-format-message="Supported file formats:
    xlsx, xls, csv, eml"
      @uploadFiles="uploadFile" />
    <div
      v-if="disableButton && archivedInvoiceDataLength"
      class="archived-invoice__upload__failed">
      <Notification
        title="One or multiple invoices have error. Please refer to the respective invoice row for errors and
Re-upload the file after correcting the invoice data."
        type="error">
      </Notification>
    </div>
    <div v-if="archivedInvoiceDataLength">
      <div :class="tableParentClass">
        <Table
          :default-columns="archivedInvoicesTableColumns"
          :row-data="archivedInvoiceData"
          primary-key="invoiceNumber"
          cell-background="#fff"
          header-cell-background="#f7f7f7"
          @addParentClass="addParentClass">
          <template #default="{ row, col }">
            <span
              v-if="col.slotKey == 'validation'"
              v-tooltip.left="{
                content: getValidationTooltip(row[col.slotKey]),
                html: true,
                classes: 'uploadTooltip',
              }"
              class="archived-invoice__upload__validation">
              <icon
                :name="row[col.slotKey] ? 'exclamation-octagon' : ''"
                class="archived-invoice__upload__validation__icon"
                :class="row[col.slotKey] ? 'error' : ''" />
              <span :class="row[col.slotKey] ? 'seperator error' : ''">
                {{ row[col.slotKey] ? "Validation Failed" : "_" }}
              </span>
            </span>
            <span
              v-else-if="col.slotKey == 'delete'"
              class="table-cell"
              @click="handleArchivedInvoiceDelete(row)">
              <icon class="delete-trash-icon" name="trash" />
            </span>
            <span
              v-else
              v-tooltip="getTableCellValueToolip(row, col)"
              class="table-cell">
              {{ getTableCellValue(row, col) }}
            </span>
          </template>
        </Table>
      </div>
      <Button
        :disabled="disableButton || disableCreateDispute"
        icon="arrow-right"
        icon-position="right"
        label="Create dispute"
        class="archived-invoice__upload__button"
        @onClick="onCreateDisputeClick" />
    </div>
    <div v-if="invoiceRecordsLoading">
      <loading
        :active.sync="invoiceRecordsLoading"
        :can-cancel="false"
        :is-full-page="true"
        color="#0573ab" />
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  archivedInvoicesTableColumns,
  excelKeyMappingForArchived,
} from "@/config/sfdcConstant";
import {
  getMsalIdToken,
  excelDateToJSDate,
  resolutionDropdownFields,
  validateEmail,
} from "@/utils/helpers";
import AxiosFetchForMsal from "@/utils/axiosForMsal";
import { disputeActions } from "@/actions";
import { showToast } from "@/components/common/Toast";
import Notification from "@anchor/ui-components/src/components/Notification/Notification.vue";
import { format } from "date-fns";
import Loading from "vue-loading-overlay";

export default {
  name: "ArchivedInvoicesUpload",
  components: {
    Notification,
    Loading,
  },
  props: {
    isBulkStatusModalVisible: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data() {
    return {
      uploadedFile: null,
      invoiceRecordsLoading: false,
      archivedInvoiceData: null,
      archivedInvoicesTableColumns,
      tableParentClass: "",
      errorMessage: "",
      disputeReasons: null,
      disableCreateDispute: false,
    };
  },
  computed: {
    archivedInvoiceDataLength() {
      return this.archivedInvoiceData?.length;
    },
    disableButton() {
      //   let validationCount = this.archivedInvoiceData?.filter(
      //     (item) => item?.validation?.length
      //   ).length;
      //   if (validationCount) {
      //     this.errorMessage = "Unable to create dispute.";
      //   }
      return this.archivedInvoiceData?.filter(
        (item) => item?.validation?.length
      ).length;
    },
  },
  mounted() {
    this.fetchDisputeReasons();
  },
  methods: {
    getTableCellValue(row, col) {
      const value = row[col.slotKey];
      if (col.type === "date") {
        try {
          return value ? format(new Date(value), "dd-MMM-yyyy hh:mm a") : "N/A";
        } catch (err) {
          return value;
        }
      }
      return !value || typeof value === "undefined" || value === "null"
        ? "-"
        : value;
    },
    getTableCellValueToolip(row, col) {
      const value = row[col.slotKey];
      if (col.type === "date") {
        try {
          return value ? format(new Date(value), "dd-MMM-yyyy hh:mm a") : "N/A";
        } catch (err) {
          return value;
        }
      } else {
        return value;
      }
    },

    addParentClass(val) {
      this.tableParentClass = val;
    },
    uploadFile(uploadedFile) {
      this.uploadedFile = uploadedFile;
      if (uploadedFile.length > 0) {
        this.parseExcel(uploadedFile);
      }
    },
    getValidationTooltip(validation) {
      if (!validation?.length) {
        return ``;
      }
      let message = [];
      validation?.length &&
        validation.forEach((item) => {
          message.push(
            `<li class="archived-tooltip__content"><span class="error">${item}</li>`
          );
        });
      return `<div class="archived-tooltip">
	  <ul>
		${message?.join("")}
	  </ul>
	</div>`;
    },
    async parseExcel(uploadedFile) {
      this.errorMessage = "";
      // Converting excel to json
      if (uploadedFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          if (json.length) {
            // Function to process & populates table from JSON
            this.processExcel(json);
          } else {
            showToast("error", "No Data found in Document");
            this.onAttachmentUploadFail();
            return false;
          }
        };
        reader.readAsArrayBuffer(uploadedFile[0].file);
      }
    },
    async fetchDisputeReasons() {
      try {
        const { data } = await AxiosFetchForMsal(
          disputeActions.disputeFieldConstants(await getMsalIdToken(this))
        );
        this.disputeReasons = resolutionDropdownFields(data, true)[
          "REASON"
        ]?.slice(1);
      } catch (error) {
        console.error(error);
      }
    },
    async processExcel(json) {
      this.invoiceRecordsLoading = true;
      let invoiceArr = [];
      // Stops execution when record length is more than maxlen(10)
      if (json.length > 50) {
        showToast("error", "Limit exceeded. Max number of record is 50");
        this.onAttachmentUploadFail();
        return false;
      }

      // Transforming excel schema to corresponding table key's
      this.archivedInvoiceData = json.map((record) => {
        let convertedObj = {};
        Object.entries(record).forEach(([key, value]) => {
          convertedObj = {
            ...convertedObj,
            [excelKeyMappingForArchived[key]]: value?.toString(),
          };
        });
        convertedObj = {
          ...convertedObj,
          reasonCode: this.disputeReasons.find(
            (reason) => reason?.displayName === convertedObj?.reasonDescription
          )?.id,
          billingDocumentDate: excelDateToJSDate(
            convertedObj.billingDocumentDate
          ),
          invoiceDueDate: excelDateToJSDate(convertedObj.invoiceDueDate),
          documentDate: excelDateToJSDate(convertedObj.documentDate),
          isArchivedInvoice: true,
        };
        invoiceArr.push(convertedObj?.invoiceNumber);
        return convertedObj;
      });
      let copyArchivedInvoiceData = this.archivedInvoiceData?.map((item) => {
        if (
          item?.noteDescription?.length > 4000 ||
          item?.noteDescription?.length === 0 ||
          item?.noteDescription == undefined ||
          item?.noteDescription === null
        ) {
          item = {
            ...item,
            validation: [
              ...(item?.validation || []),
              "Comment field must be between 1 - 4000 characters",
            ],
          };
        }
        if (!item?.contactEmail?.length || !validateEmail(item?.contactEmail)) {
          item = {
            ...item,
            validation: [
              ...(item?.validation || []),
              "Contact email is invalid",
            ],
          };
        }
        return item;
      });
      this.archivedInvoiceData = copyArchivedInvoiceData;
      this.fetchInvoiceDisputeDetails(invoiceArr);
    },
    onAttachmentUploadFail() {
      this.invoiceRecordsLoading = false;
    },
    clearErrorMessage() {
      this.errorMessage = "";
    },
    async onCreateDisputeClick() {
      this.disableCreateDispute = true;
      if (!this.archivedInvoiceData?.length) {
        return;
      }
      try {
        const { data } = await AxiosFetchForMsal(
          disputeActions.createDisputeArchived(
            {
              data: this.archivedInvoiceData,
            },
            await getMsalIdToken(this)
          )
        );
        localStorage.setItem("bulk_create_id", data);
        this.$emit("setBulkCreatedId", data);
        showToast("success", "Dispute created successfully");
        this.disableCreateDispute = false;
      } catch (error) {
        if (error?.response?.status === 400) {
          let copyArchivedInvoiceData = this.archivedInvoiceData;
          Object.keys(error?.response?.data).forEach((elem) => {
            copyArchivedInvoiceData = copyArchivedInvoiceData?.map((item) => {
              if (item?.invoiceNumber == elem) {
                item = {
                  ...item,
                  validation: error?.response?.data?.[elem],
                };
              }
              return item;
            });
          });
          this.archivedInvoiceData = copyArchivedInvoiceData;
        }
        showToast("error", "Unable to create dispute. please try again");
        this.disableCreateDispute = false;
      }
    },
    async fetchInvoiceDisputeDetails(invoiceArr) {
      let eccInvoiceArr = [];
      let s4InvoiceArr = [];
      // Sorting ECC & S4 invoices
      invoiceArr?.forEach((invoiceNumber) => {
        if (/^[59].*$/i.test(invoiceNumber)) {
          eccInvoiceArr.push(invoiceNumber);
        } else {
          s4InvoiceArr.push(invoiceNumber);
        }
      });
      const invoiceFetch = [
        {
          type: "ecc",
          invoice: eccInvoiceArr,
        },
        {
          type: "s4",
          invoice: s4InvoiceArr,
        },
        {
          type: "dispute",
          invoice: [...eccInvoiceArr, ...s4InvoiceArr],
        },
      ];
      const msalToken = await getMsalIdToken(this);
      let promiseArray = [];
      //  Creating multiple API call's
      invoiceFetch.forEach((item) => {
        const invoiceArr = item.invoice;
        const isDispute = item.type === "dispute";
        if (isDispute) {
          // API to get details for already created invoice
          promiseArray.push(
            disputeActions.getDisputeDetailsFromInvoiceId(
              msalToken,
              invoiceArr?.toString(),
              0,
              200
            )
          );
        } else {
          // API to validate invoice and fetch details
          promiseArray.push(
            disputeActions.getDisputeInvoiceDetailsFromSap(
              msalToken,
              invoiceArr?.toString(),
              0,
              2000
            )
          );
        }
      });
      let invoiceList = [];
      let disputeList = [];
      // Parallel API call's
      await Promise.allSettled(
        promiseArray?.map((endpoint) => AxiosFetchForMsal(endpoint))
      )
        .then((res) => {
          res.forEach((item) => {
            if (item.status === "rejected") {
              return false;
            }
            const data = item?.value?.data || [];
            // SAP response
            if (Object.keys(data).includes("invoiceList")) {
              invoiceList.push(...data.invoiceList);
            }
            //Dispute api response
            if (Object.keys(data).includes("content")) {
              disputeList.push(...data.content);
            }
          });
        })
        .catch((e) => {
          console.error("e", e);
          this.onAttachmentUploadFail();
          return false;
        });
      let copyArchivedInvoiceData = this.archivedInvoiceData?.map((item) => {
        const findIndex = disputeList?.findIndex((dispute) => {
          return dispute?.invoiceNumber == item?.invoiceNumber;
        });
        if (findIndex > -1) {
          item = {
            ...item,
            validation: [
              ...(item?.validation || []),
              "Dispute already exist with the given invoice no",
            ],
          };
        }
        const findIndexInInvoiceList = invoiceList?.findIndex((dispute) => {
          return dispute?.invoiceNumber == item?.invoiceNumber;
        });
        if (findIndexInInvoiceList > -1) {
          item = {
            ...item,
            validation: [
              ...(item?.validation || []),
              "Invoice Number is not archived",
            ],
          };
        }
        return item;
      });
      this.archivedInvoiceData = copyArchivedInvoiceData;
      this.onAttachmentUploadFail();
    },

    handleArchivedInvoiceDelete(row) {
      const index = this.archivedInvoiceData.findIndex((item) => {
        return item.invoiceNumber === row?.invoiceNumber;
      });
      if (index >= 0) {
        this.archivedInvoiceData.splice(index, 1);
      }
    },
  },
};
</script>

<style lang="scss">
.archived-invoice__upload {
  &__button {
    margin-top: 24px;
    float: right;
  }
  &__failed {
    margin-bottom: 20px;
  }
  .table-parent-style {
    overflow: auto;
    border: 1px solid #e8eaec;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 10px;
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #888;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
  .error {
    color: var(--Danger);
  }
  &__validation {
    display: flex;
    cursor: pointer;
    &__button-wrapper {
      display: flex;

      i {
        cursor: pointer;
        margin-left: 10px;
      }
    }
    &__icon {
      margin-top: 2px;
      align-items: center;
      margin-right: 4px;
      &__error {
        color: var(--Danger);
      }
    }
    &__loader-wrapper {
      display: flex;
      flex-direction: column;
      &__loader-button {
        border: 0;
        background: transparent;
        width: 0;
        margin: 0 auto;
        pointer-events: none;
        &:after {
          border-top-color: var(--Blue-300-Maersk) !important;
        }
      }
    }
    .seperator {
      text-decoration: underline;
    }
  }
}
.validation {
  align-items: end;
  padding: 10px 12px;
}
.uploadTooltip {
  display: flex;
  justify-content: center;
  .tooltip-inner {
    background: var(--Gray-50);
    border: 1px solid var(--Gray-300);
    width: max-content;
    max-width: 350px;
    height: max-content;
    padding: 16px 8px;
    color: var(--Gray-700);
    .archived-tooltip {
      ul {
        margin: 0;
        padding: 0 30px;
      }
      &__content {
        font-family: "Maersk Text";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        color: var(--Gray-700);
        margin: 4px 0;
      }
      .error {
        color: var(--Danger);
      }
      .error-icon-message {
        font-family: "Maersk Text";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
      }
    }
  }
}
.delete-trash-icon {
  cursor: pointer;
}
</style>
